import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import {
  MenuAlt2Icon,
  XIcon,
} from '@heroicons/react/outline'
import AppViews from 'views/app-views'
import NavProfile from 'components/layout-components/NavProfile'
import { useMenu } from 'hooks/useMenu';
import useDomain from 'hooks/useDomain';
import MenuItem from 'components/layout-components/MenuItem';
import { SupportSVG } from 'assets/svg/icon';
import { classNames } from 'utils'
import defaultLogo from 'assets/logo/default.png'

export default function AppLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const { mainNavTree } = useMenu()
  const { domainData } = useDomain()

  return (
    <div className='min-h-screen bg-gradient-to-br from-monko-50 via-slate-50 to-monko-100/30 p-4 lg:p-6 max-w-full overflow-hidden relative'>
      <div className="absolute inset-0 bg-skew-pattern opacity-[0.03] pointer-events-none" />
      <div className="relative z-10">
        <Transition.Root show={mobileMenuOpen} as={Fragment}>
          <Dialog as="div" className="lg:hidden" onClose={setMobileMenuOpen}>
            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                as={Fragment}
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" />
              </Transition.Child>
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <div className="relative max-w-xs w-full bg-white/95 backdrop-blur-lg border-r border-white/20 pt-5 pb-4 flex-1 flex flex-col shadow-2xl">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute top-2 right-2 p-1">
                      <button
                        type="button"
                        className="h-9 w-9 rounded-xl flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-all"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <XIcon className="h-5 w-5" aria-hidden="true" />
                        <span className="sr-only">Close sidebar</span>
                      </button>
                    </div>
                  </Transition.Child>
                  <div className="flex-shrink-0 px-6 py-4 flex items-center border-b border-monko/10">
                    <img
                      className="w-auto h-12 object-contain"
                      src={domainData.logo}
                      alt="Logo"
                    />
                  </div>
                  <div className="mt-4 flex-1 h-0 px-4 overflow-y-auto">
                    <nav className="h-full flex flex-col">
                      <ul className="space-y-1">
                        {mainNavTree.map((item, index) => (
                          <MenuItem
                            key={index}
                            menu={item}
                            onClick={() => setMobileMenuOpen(false)}
                          />
                        ))}
                      </ul>
                    </nav>
                  </div>
                </div>
              </Transition.Child>
              <div className="flex-shrink-0 w-14" aria-hidden="true">
              </div>
            </div>
          </Dialog>
        </Transition.Root>
        <div className="relative mt-4">
          <div className="flex">
            <nav className="hidden lg:block sticky top-6 h-[calc(100vh-3rem)] w-[280px] bg-white/70 backdrop-blur-md border border-white/60 shadow-xl rounded-[2rem] px-5 py-8 overflow-y-auto mr-6 flex-shrink-0 transition-all duration-300">
              <div className="flex items-center justify-center mb-8 pb-6 border-b border-monko/10">
                <img
                  alt="logo"
                  className="h-12 w-auto object-contain transition-transform duration-300 hover:scale-105"
                  src={domainData.logo}
                />
              </div>
              <ul className="space-y-1">
                {mainNavTree.map((menu, index) => (
                  <MenuItem menu={menu} key={index} />
                ))}
              </ul>
            </nav>
            <div className="flex-grow min-w-0 bg-white/80 backdrop-blur-sm border border-white/50 shadow-xl rounded-[2rem] min-h-[calc(100vh-3rem)] pb-10 flex flex-col transition-all duration-300">
              <header className="h-[76px] flex items-center justify-between lg:justify-end px-8 border-b border-monko/10">
                <button
                  type="button"
                  className="p-2 text-monko lg:hidden rounded-xl hover:bg-monko/10 transition-colors duration-200"
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <span className="sr-only">Open sidebar</span>
                  <MenuAlt2Icon className="h-6 w-6" aria-hidden="true" />
                </button>
                <div className="flex items-center space-x-4">
                  <button className="p-2 rounded-xl hover:bg-monko/10 text-monko transition-all duration-200 hover:scale-105">
                    <SupportSVG className="h-6 w-6 fill-current" />
                  </button>
                  <div className="h-8 w-px bg-monko/10" />
                  <NavProfile />
                </div>
              </header>
              <main className="flex-grow p-6 md:p-8">
                <AppViews />
              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}